import * as React from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Note,
  Spacer,
  Text,
  Textarea,
  VStack,
} from '@packages/systems/spring';

import {
  parseBulkListItems,
  type BulkListItem,
  type BulkListItemError,
} from './parseBulkListItems';

type Props = {
  isOpen: boolean;
  /**
   * Called with the parsed items, plus the text they were parsed from, once the
   * paste passes validation.
   */
  onCreate: (items: BulkListItem[], meta: {rawText: string}) => void;
  onClose: () => void;
};

export const BULK_ADD_OPTIONS_LABEL = 'Add options in bulk';

const TITLE = BULK_ADD_OPTIONS_LABEL;
const HELP_TEXT =
  'Enter one option per line. Separate the text and value with a tab or a comma. If you omit the value, the text is used as the value.';
const PLACEHOLDER =
  'United States, united-states\nCanada, canada\nMexico, mexico';

export const BulkAddListItemsModal = ({isOpen, onCreate, onClose}: Props) => {
  const [text, setText] = React.useState('');
  const [errors, setErrors] = React.useState<BulkListItemError[]>([]);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Reset to a clean slate each time the modal opens so a previous (possibly
  // failed) attempt doesn't leak into the next one.
  React.useEffect(() => {
    if (isOpen) {
      setText('');
      setErrors([]);
    }
  }, [isOpen]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    // Clear stale errors while editing; line numbers are recomputed on submit.
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const isEmpty = text.trim() === '';

  const handleSubmit = () => {
    const result = parseBulkListItems(text);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    onCreate(result.items, {rawText: text});
    onClose();
  };

  // Cmd/Ctrl + Enter submits, so a paste can be confirmed without reaching for
  // the mouse. A bare Enter still inserts a newline, since options are one
  // per line.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && !isEmpty) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hasCloseButton
      // Clicking the canvas behind the modal shouldn't discard a paste in
      // progress; Cancel, the close button, and Escape remain the ways out.
      shouldHideOnClickOutside={false}
      aria-label={TITLE}
      onOpenFocusRef={textareaRef}
    >
      <ModalHeader title={TITLE} />
      <ModalBody>
        <VStack spacing={3}>
          <Note
            colorVariant="default"
            data-automation-id="BulkAddListItemsHelp"
          >
            {HELP_TEXT}
          </Note>
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={PLACEHOLDER}
            rows={10}
            fontFamily="mono"
            resize="vertical"
            isError={errors.length > 0}
            aria-label={TITLE}
            data-automation-id="BulkAddListItemsTextarea"
          />
          {errors.length > 0 ? (
            <Note
              colorVariant="danger"
              title="Fix these errors to continue:"
              data-automation-id="BulkAddListItemsErrors"
            >
              <VStack spacing={1}>
                {errors.map((error, index) => (
                  <Text key={`${error.line}-${index}`}>{error.message}</Text>
                ))}
              </VStack>
            </Note>
          ) : null}
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose} data-automation-id="BulkAddListItemsCancel">
          Cancel
        </Button>
        <Spacer />
        <Button
          colorVariant="primary"
          onClick={handleSubmit}
          isDisabled={isEmpty}
          data-automation-id="BulkAddListItemsSubmit"
        >
          Add options
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkAddListItemsModal;
